// src/lessons/podtemat1_liniowe/Lesson1_TeoriaPrzyklady.js
import React from "react";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import { BlockMath, InlineMath } from "react-katex";
import Latex from "react-latex-next";

export default function RownaniaLiniowe_TeoriaPrzyklady() {
  return (
    <TheoryExamplesRunner
intro={
  <>
    <p>
      <strong>Równanie liniowe</strong> to równanie z&nbsp;jedną niewiadomą
      pierwszego stopnia. Zazwyczaj dąży się w&nbsp;nim do znalezienia takiej wartości
      niewiadomej, dla której równość jest prawdziwa.
    </p>

    <p>
      <strong>Metoda rozwiązywania:</strong>
    </p>
    <ol style={{ marginLeft: 18, lineHeight: 1.6 }}>
      <li>
        Zredukuj obie strony (usuń nawiasy, uporządkuj): zsumuj składniki
        z&nbsp;<InlineMath math="x" /> oraz liczby stałe.
      </li>
      <li>
        Przenieś wszystkie wyrazy z&nbsp;<InlineMath math="x" /> na jedną stronę,
        a&nbsp;liczby na drugą.
      </li>
      <li>
        Otrzymasz postać <InlineMath math="A x = B" />. Podziel obie strony przez{" "}
        <InlineMath math="A \neq 0" /> i&nbsp;otrzymaj{" "}
        <InlineMath math={'x = \\dfrac{B}{A}'} />.
      </li>
    </ol>

    <p className="mt-3">
      <strong>Klasyfikacja równań liniowych:</strong>
    </p>

    <p>
      <strong>Równanie oznaczone</strong> — to takie, które ma dokładnie jedno rozwiązanie.
      Jest to klasyczny przypadek równania liniowego, np.{" "}
      <InlineMath math="3x - 2 = 7" />, gdzie po przekształceniach otrzymujemy jedno konkretne{" "}
      <InlineMath math="x" />.
    </p>

    <p>
      <strong>Równanie tożsamościowe</strong> — to takie, w&nbsp;którym po przekształceniach
      niewiadoma <InlineMath math="x" /> się&nbsp;skraca, a&nbsp;otrzymujemy{" "}
      <em>prawdę</em>, np. <InlineMath math="0 = 0" />. Takie równanie jest spełnione
      dla każdego <InlineMath math="x" />.
    </p>

    <p>
      <strong>Równanie sprzeczne</strong> — to takie, które po przekształceniach
      prowadzi do <em>sprzeczności</em>, np. <InlineMath math="0 = 5" />.
      Takie równanie nie ma żadnych rozwiązań.
    </p>
  </>
}


      examples={[
        /* =========================================================
           PRZYKŁAD 1 — proste ax+b=c
        ========================================================== */
        {
          label: "Przykład 1",
          summary: "Równanie proste — jedna strona liczbowa",
          description: (
            <Latex>{`Rozwiąż równanie $3x - 7 = 11$.`}</Latex>
          ),
          content: (
            <div className="space-y-2">
              <p>
                <strong>Krok 1:</strong> Przenieś <InlineMath math="-7" /> na
                prawą stronę.
              </p>
              <BlockMath math={"3x - 7 = 11\\;\\Big|\\ +\\ 7"} />
              <BlockMath math={"3x = 18"} />
              <p>
                <strong>Krok 2:</strong> Podziel przez{" "}
                <InlineMath math="3" />.
              </p>
              <BlockMath math={"3x = 18\\;\\Big|\\ :\\ 3"} />
              <BlockMath math={"x = 6"} />
            </div>
          ),
        },

        /* =========================================================
           PRZYKŁAD 2 — po obu stronach ax+b=cx+d
        ========================================================== */
        {
          label: "Przykład 2",
          summary: "Równanie obustronne",
          description: (
            <Latex>{`Rozwiąż równanie $5x + 2 = 2x - 10$.`}</Latex>
          ),
          content: (
            <div className="space-y-2">
              <p>
                <strong>Krok 1:</strong> Przenieś składniki z{" "}
                <InlineMath math="x" /> na lewo.
              </p>
              <BlockMath math={"5x + 2 = 2x - 10\\;\\Big|\\ -\\ 2x"} />
              <BlockMath math={"3x + 2 = -10"} />
              <p>
                <strong>Krok 2:</strong> Przenieś liczby na prawo.
              </p>
              <BlockMath math={"3x + 2 = -10\\;\\Big|\\ -\\ 2"} />
              <BlockMath math={"3x = -12"} />
              <p>
                <strong>Krok 3:</strong> Podziel przez{" "}
                <InlineMath math="3" />.
              </p>
              <BlockMath math={"x = -4"} />
            </div>
          ),
        },

        /* =========================================================
           PRZYKŁAD 3 — z nawiasami
        ========================================================== */
        {
          label: "Przykład 3",
          summary: "Równanie z nawiasami po obu stronach",
          description: (
            <Latex>{`Rozwiąż równanie $2(x-3)+1 = -(x+2)+7$.`}</Latex>
          ),
          content: (
            <div className="space-y-2">
              <p>
                <strong>Krok 1:</strong> Rozwiń nawiasy i uporządkuj.
              </p>
              <BlockMath math={"2(x-3)+1 = 2x-6+1 = 2x-5"} />
              <BlockMath math={"-(x+2)+7 = -x-2+7 = -x+5"} />
              <BlockMath math={"2x-5 = -x+5"} />
              <p>
                <strong>Krok 2:</strong> Przenieś <InlineMath math="x" /> na
                lewo, liczby na prawo.
              </p>
              <BlockMath math={"2x-5 = -x+5\\;\\Big|\\ +\\ x,\\ +\\ 5"} />
              <BlockMath math={"3x = 10"} />
              <p>
                <strong>Krok 3:</strong> Podziel przez{" "}
                <InlineMath math="3" />.
              </p>
              <BlockMath math={"x = \\dfrac{10}{3}"} />
            </div>
          ),
        },

        /* =========================================================
           PRZYKŁAD 4 — „kwadratowe” redukujące się do liniowego
        ========================================================== */
        {
          label: "Przykład 4",
          summary:
            "Po rozwinięciu składowe kwadratowe się skracają — zostaje liniowe",
          description: (
            <Latex>{`Rozwiąż równanie $(x+2)(x-3) = (x+1)^2 - 1$.`}</Latex>
          ),
          content: (
            <div className="space-y-2">
              <p>
                <strong>Krok 1:</strong> Rozwiń obie strony.
              </p>
              <BlockMath math={"(x+2)(x-3)=x^2 - x - 6"} />
              <BlockMath math={"(x+1)^2 - 1 = (x^2+2x+1)-1 = x^2+2x"} />
              <p>
                <strong>Krok 2:</strong> Odejmij{" "}
                <InlineMath math="x^2" /> po obu stronach (skróci kwadraty).
              </p>
              <BlockMath math={"x^2 - x - 6 = x^2 + 2x\\;\\Big|\\ -\\ x^2"} />
              <BlockMath math={"-x - 6 = 2x"} />
              <p>
                <strong>Krok 3:</strong> Rozwiąż liniowe.
              </p>
              <BlockMath math={"-x - 6 = 2x\\;\\Big|\\ +\\ x"} />
              <BlockMath math={"-6 = 3x\\;\\Big|\\ :\\ 3"} />
              <BlockMath math={"x = -2"} />
            </div>
          ),
        },

        /* =========================================================
           PRZYKŁAD 5A — tożsamościowe
        ========================================================== */
        {
          label: "Przykład 5A",
          summary: "Tożsamość — prawda dla każdego x",
          description: (
            <Latex>{`Oceń typ równania: $(x+1)(x-1)+1 = x^2$.`}</Latex>
          ),
          content: (
            <div className="space-y-2">
              <p>
                <strong>Krok 1:</strong> Rozwiń lewą stronę.
              </p>
              <BlockMath math={"(x+1)(x-1)+1 = (x^2-1)+1 = x^2"} />
              <p>
                <strong>Krok 2:</strong> Obie strony są równe{" "}
                <InlineMath math="x^2" />. Po odjęciu{" "}
                <InlineMath math="x^2" /> dostajemy:
              </p>
              <BlockMath math={"x^2 = x^2\\;\\Big|\\ -\\ x^2"} />
              <BlockMath math={"0 = 0"} />
              <p>
                To <strong>równanie tożsamościowe</strong> — prawdziwe dla
                każdego <InlineMath math="x" />.
              </p>
            </div>
          ),
        },

        /* =========================================================
           PRZYKŁAD 5B — sprzeczne
        ========================================================== */
        {
          label: "Przykład 5B",
          summary: "Sprzeczność — brak rozwiązań",
          description: (
            <Latex>{`Oceń typ równania: $x - (x - 4) = 5$.`}</Latex>
          ),
          content: (
            <div className="space-y-2">
              <p>
                <strong>Krok 1:</strong> Rozwiń i zredukuj.
              </p>
              <BlockMath math={"x - (x - 4) = x - x + 4 = 4"} />
              <p>
                <strong>Krok 2:</strong> Otrzymujemy{" "}
                <InlineMath math="4 = 5" /> — fałsz niezależny od{" "}
                <InlineMath math="x" />.
              </p>
              <BlockMath math={"4 = 5"} />
              <p>
                To <strong>równanie sprzeczne</strong> —{" "}
                <em>nie ma rozwiązań</em>.
              </p>
            </div>
          ),
        },
      ]}
    />
  );
}
